<!-- Alert Container -->
<app-alert-container></app-alert-container>

<!-- Connection Error -->
@if (statusError() && !statusLoading()) {
  <div class="alert alert-warning">
    <i class="fa fa-exclamation-triangle"></i>
    <strong>Unable to connect to Image Sync service</strong>
    <p class="m-t-sm">{{ statusError() }}</p>
    <button class="btn btn-sm btn-white m-t-sm" (click)="refreshAll()" [disabled]="statusLoading()">
      <i class="fa fa-refresh"></i> Retry
    </button>
  </div>
}

<!-- Status Dashboard -->
<div class="row m-b-md">
  <div class="col-lg-12">
    <div class="d-flex-between">
      <h4 class="section-title">
        Sync Status
        @if (statusLoading()) {
          <i class="fa fa-spinner fa-spin m-l-sm"></i>
        }
      </h4>
      <div class="status-actions">
        <button class="btn btn-sm btn-white" (click)="refreshAll()" [disabled]="statusLoading() || actionLoading()">
          <i class="fa fa-refresh"></i> Refresh
        </button>
        <label class="auto-refresh-toggle m-l-sm">
          <input type="checkbox" [checked]="autoRefresh()" (change)="toggleAutoRefresh()" />
          Auto (30s)
        </label>
      </div>
    </div>
  </div>
</div>

@if (status()) {
  <div class="row m-b-md">
    <div class="col-md-3">
      <div class="widget style1 navy-bg">
        <div class="row">
          <div class="col-xs-4">
            <i class="fa fa-heartbeat fa-3x"></i>
          </div>
          <div class="col-xs-8 text-right">
            <span class="widget-label">Status</span>
            <h2 class="font-bold">
              <span class="label" [ngClass]="statusBadgeClass()">{{ statusBadgeText() }}</span>
            </h2>
          </div>
        </div>
      </div>
    </div>
    <div class="col-md-3">
      <div class="widget style1 lazur-bg">
        <div class="row">
          <div class="col-xs-4">
            <i class="fa fa-clock-o fa-3x"></i>
          </div>
          <div class="col-xs-8 text-right">
            <span class="widget-label">Pending</span>
            <h2 class="font-bold">{{ status()!.pendingJobs }}</h2>
          </div>
        </div>
      </div>
    </div>
    <div class="col-md-3">
      <div class="widget style1 red-bg">
        <div class="row">
          <div class="col-xs-4">
            <i class="fa fa-exclamation-circle fa-3x"></i>
          </div>
          <div class="col-xs-8 text-right">
            <span class="widget-label">Failed</span>
            <h2 class="font-bold">{{ status()!.failedJobs }}</h2>
          </div>
        </div>
      </div>
    </div>
    <div class="col-md-3">
      <div class="widget style1 yellow-bg">
        <div class="row">
          <div class="col-xs-4">
            <i class="fa fa-check-circle fa-3x"></i>
          </div>
          <div class="col-xs-8 text-right">
            <span class="widget-label">Synced 24h</span>
            <h2 class="font-bold">{{ status()!.totalSynced24h }}</h2>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="row m-b-md status-row">
    <div class="col-md-8">
      <div class="status-details">
        <span class="detail-item">
          <i class="fa fa-clock-o"></i> <strong>Last Sync:</strong> {{ formatDate(status()!.lastSyncAt) }}
        </span>
        @if (status()!.cursor) {
          <span class="detail-item">
            <i class="fa fa-folder-o"></i> <strong>Watched Folder:</strong> {{ status()!.cursor!.folderPath }}
          </span>
          <span class="detail-item">
            <i class="fa fa-calendar"></i> <strong>Cursor Updated:</strong> {{ formatDate(status()!.cursor!.lastUpdated) }}
          </span>
        }
      </div>
    </div>
    <div class="col-md-4 text-right actions-col">
      <button class="btn btn-sm btn-primary" (click)="triggerFullSync()" [disabled]="actionLoading()"
        title="Resets the Dropbox cursor and re-processes all files. Only changed files are re-uploaded to S3.">
        <i class="fa fa-refresh"></i> Full Sync
      </button>
      <button class="btn btn-sm btn-info" (click)="reconcileDryRun()" [disabled]="actionLoading()"
        title="Compares S3 objects against Dropbox files to find orphaned S3 objects that no longer exist in Dropbox. Preview only — nothing is deleted.">
        <i class="fa fa-search"></i> Reconcile
      </button>
      @if (isDevEnvironment()) {
        <button class="btn btn-sm btn-default" (click)="actionsCollapsed.set(!actionsCollapsed())"
          title="Show/hide dev actions">
          <i class="fa" [ngClass]="actionsCollapsed() ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
        </button>
      }
    </div>
  </div>

  @if (isDevEnvironment() && !actionsCollapsed()) {
    <div class="row m-b-md">
      <div class="col-lg-12">
        <div class="dev-actions-section">
          <small class="text-muted"><i class="fa fa-warning"></i> Dev/Local Only</small>
          <div class="dev-actions-inline m-t-xs">
            <label class="checkbox-inline">
              <input type="checkbox" [checked]="resetIncludeCursor()" (change)="resetIncludeCursor.set($any($event.target).checked)" />
              Include cursor
            </label>
            <label class="checkbox-inline">
              <input type="checkbox" [checked]="resetIncludeHistory()" (change)="resetIncludeHistory.set($any($event.target).checked)" />
              Include history
            </label>
            <button class="btn btn-danger btn-xs m-l-sm" (click)="resetSync()" [disabled]="actionLoading()"
              title="Clears outbox/inbox message queues and optionally resets cursor position and sync history. Use to clean up state during development.">
              <i class="fa fa-warning"></i> Reset Sync State
            </button>
          </div>
        </div>
      </div>
    </div>
  }
}

<!-- Reconcile Results Panel -->
@if (reconcileResult()) {
  <div class="row m-b-md">
    <div class="col-lg-12">
      <div class="ibox">
        <div class="ibox-title">
          <h5>Reconciliation Preview</h5>
          <div class="ibox-tools">
            <a (click)="closeReconcilePanel()" title="Close">
              <i class="fa fa-times"></i>
            </a>
          </div>
        </div>
        <div class="ibox-content">
          <div class="row m-b-md">
            <div class="col-md-4">
              <strong>Dropbox Files:</strong> {{ reconcileResult()!.dropboxFileCount | number }}
            </div>
            <div class="col-md-4">
              <strong>S3 Objects:</strong> {{ reconcileResult()!.s3ObjectCount | number }}
            </div>
            <div class="col-md-4">
              <strong>Orphaned S3 Objects:</strong>
              <span [class.text-danger]="reconcileResult()!.orphanedKeys.length > 0">
                {{ reconcileResult()!.orphanedKeys.length }}
              </span>
            </div>
          </div>

          @if (reconcileResult()!.orphanedKeys.length > 0) {
            <div class="orphan-list m-b-md">
              <label>Orphaned Keys:</label>
              <div class="well well-sm orphan-scroll">
                @for (key of reconcileResult()!.orphanedKeys; track key) {
                  <div class="orphan-item">{{ key }}</div>
                }
              </div>
            </div>
            <button class="btn btn-danger" (click)="reconcileDelete()" [disabled]="actionLoading()">
              <i class="fa fa-trash"></i> Delete {{ reconcileResult()!.orphanedKeys.length }} Orphaned Objects
            </button>
          } @else {
            <div class="alert alert-success m-b-none">
              <i class="fa fa-check-circle"></i> No orphaned objects found. S3 and Dropbox are in sync.
            </div>
          }
        </div>
      </div>
    </div>
  </div>
}

<!-- History Table -->
<div class="row m-b-md">
  <div class="col-lg-12">
    <h4 class="section-title">
      Sync History
      @if (historyLoading()) {
        <i class="fa fa-spinner fa-spin m-l-sm"></i>
      }
    </h4>

    <!-- Filters -->
    <div class="row m-b-sm">
      <div class="col-md-2">
        <label class="control-label">From</label>
        <input
          type="date"
          class="form-control input-sm"
          [value]="dateFrom() ?? ''"
          (change)="dateFrom.set($any($event.target).value || null); onDateFilterChange()" />
      </div>
      <div class="col-md-2">
        <label class="control-label">To</label>
        <input
          type="date"
          class="form-control input-sm"
          [value]="dateTo() ?? ''"
          (change)="dateTo.set($any($event.target).value || null); onDateFilterChange()" />
      </div>
      <div class="col-md-2">
        <label class="control-label">Status</label>
        <select
          class="form-control input-sm"
          (change)="onSuccessFilterChange($any($event.target).value)">
          <option value="all" [selected]="successFilter() === null">All</option>
          <option value="success" [selected]="successFilter() === true">Success Only</option>
          <option value="failures" [selected]="successFilter() === false">Failures Only</option>
        </select>
      </div>
      <div class="col-md-2">
        <label class="control-label">Rows</label>
        <select
          class="form-control input-sm"
          [value]="pageSize()"
          (change)="onPageSizeChange($any($event.target).value)">
          <option value="5">5</option>
          <option value="10">10</option>
          <option value="20">20</option>
          <option value="50">50</option>
        </select>
      </div>
    </div>

    <!-- History Error -->
    @if (historyError() && !historyLoading()) {
      <div class="alert alert-warning">
        <i class="fa fa-exclamation-triangle"></i>
        Failed to load sync history: {{ historyError() }}
      </div>
    }

    <!-- Table -->
    @if (history() && history()!.items.length > 0) {
      <div class="table-responsive">
        <table class="table table-striped table-hover">
          <thead>
            <tr>
              <th class="col-dropbox">Dropbox Path</th>
              <th class="col-s3key">S3 Key</th>
              <th class="col-type">Type</th>
              <th class="col-size">Size</th>
              <th class="col-duration">Duration</th>
              <th class="col-synced">Synced At</th>
              <th class="col-status">Status</th>
            </tr>
          </thead>
          <tbody>
            @for (item of history()!.items; track item.id) {
              <tr [class.danger]="!item.success">
                <td class="truncate-cell" [title]="item.dropboxPath">{{ item.dropboxPath }}</td>
                <td class="truncate-cell" [title]="item.s3Key">{{ item.s3Key }}</td>
                <td>
                  <span class="label" [ngClass]="getChangeTypeBadgeClass(item.changeType)">
                    <i [class]="getChangeTypeIcon(item.changeType)"></i> {{ item.changeType }}
                  </span>
                </td>
                <td>{{ formatFileSize(item.fileSizeBytes) }}</td>
                <td>{{ formatDuration(item.durationMs) }}</td>
                <td>{{ formatDate(item.syncedAt) }}</td>
                <td>
                  @if (item.success) {
                    <span class="text-success"><i class="fa fa-check"></i></span>
                  } @else {
                    <span class="text-danger" [title]="item.errorMessage || ''">
                      <i class="fa fa-times"></i>
                      @if (item.errorMessage) {
                        <small class="m-l-xs">{{ item.errorMessage }}</small>
                      }
                    </span>
                  }
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <app-table-pagination
        [paginationData]="paginationData()"
        [itemDescriptor]="'History items'"
        (pageChange)="onPageChange($event)">
      </app-table-pagination>
    } @else if (!historyLoading() && !historyError()) {
      <div class="alert alert-info">
        <i class="fa fa-info-circle"></i>
        No sync history records found.
      </div>
    }
  </div>
</div>

<!-- Action Loading Overlay -->
@if (actionLoading()) {
  <div class="action-loading-overlay">
    <i class="fa fa-spinner fa-spin fa-2x"></i>
    <span class="m-l-sm">Processing...</span>
  </div>
}
